import { Box } from "@mui/system";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { Grid, List, ListItem, Typography } from "@mui/material";
import { Check, Clear } from "@mui/icons-material";
import PrimaryButton from "../../components/custom/PrimaryButton";
import TextField from "../../components/custom/TextField";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import React from "react";
import { useNavigate } from "react-router-dom";

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    width: 300,
    // height: 140,
    height: (props) => 100 * props.ratio,
    border: "1px solid #dadde9",
    borderRadius: "1rem",
    margin: 0,
    display: "flex",
    flexDirection: "column",
  },
}));

const ListWrapper = styled(List)`
  height: 20%;
  padding: 0;
  li {
    padding: 0;
  }
  margin-left: 1rem;
`;

const ListItemsWrapper = styled(Box)`
  display: flex;
  align-items: center;
`;
const CheckIconWrapper = styled(Check)`
  margin-left: 0.3rem;
  font-size: 0.8rem;
  color: green;
`;

const ClearIconWrapper = styled(Clear)`
  margin-left: 0.3rem;
  font-size: 0.8rem;
  color: red;
`;

const LabelWrapper = styled(Box)`
  display: flex;
  align-items: center;
`;

const EightChars = /.{8,}/;
const UpperCaseCharacter = /(.*[A-Z].*)/;
const LowerCaseCharacter = /(.*[a-z].*)/;
const AtLeastOneDigit = /(.*\d.*)/;
const SpecialCharacter = /(.*\W.*)/;
const AllMatches =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

const UserForm = ({
  edit = false,
  onSubmit = () => {},
  formikRef,
  data = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    password2: "",
    userRole: 3,
    userName: "",
    active: false,
    edit: false,
  },
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // const [tooltipIsOpen, setTooltipIsOpen] = useState(false);

  const validationSchema = Yup.object().shape({
    edit: Yup.boolean(),
    firstName: Yup.string().required("Required field"),
    lastName: Yup.string().required("Required field"),
    email: Yup.string().email().required("Required field"),
    password: Yup.string()
      .required("Required field")
      .matches(AllMatches, t("Please type password based on requirements")),
    password2: Yup.string().when("edit", {
      is: false,
      then: Yup.string()
        .required("Required field")
        .oneOf([Yup.ref("password"), null], "Passwords must match"),
    }),
  });

  return (
    <Box sx={{ mb: 10 }}>
      <Formik
        validationSchema={validationSchema}
        innerRef={formikRef}
        enableReinitialize
        initialValues={data}
        onSubmit={onSubmit}
      >
        {({ submitForm, errors, values, touched, dirty }) => (
          <Form autoComplete="off">
            <Grid container xs={12}>
              <Grid container xs={6}>
                <TextField
                  label={"First name"}
                  name="firstName"
                  placeholder={"Type the first name"}
                />
                <TextField
                  label={"Last name"}
                  name="lastName"
                  placeholder={"Type the last name"}
                />
                <TextField
                  label={"Email"}
                  name="email"
                  disabled={edit}
                  placeholder={"Type the email"}
                />
                <TextField
                  label={
                    <LabelWrapper>
                      <Typography variant="h9">{"Password"}</Typography>
                      {!edit && (
                        <HtmlTooltip
                          placement="right-end"
                          title={
                            <>
                              <Typography
                                sx={{ fontSize: "0.7rem", fontWeight: "600" }}
                              >
                                {t("Password should contain")}
                              </Typography>
                              <ListWrapper sx={{ listStyleType: "disc" }}>
                                <ListItem sx={{ display: "list-item" }}>
                                  <ListItemsWrapper>
                                    {t("At least 8 characters")}
                                    {values?.password?.match(EightChars) ? (
                                      <CheckIconWrapper />
                                    ) : (
                                      <ClearIconWrapper />
                                    )}
                                  </ListItemsWrapper>
                                </ListItem>
                                <ListItem sx={{ display: "list-item" }}>
                                  <ListItemsWrapper>
                                    {t("At least one upper case letter")}
                                    {values?.password?.match(
                                      UpperCaseCharacter
                                    ) ? (
                                      <CheckIconWrapper />
                                    ) : (
                                      <ClearIconWrapper />
                                    )}
                                  </ListItemsWrapper>
                                </ListItem>
                                <ListItem sx={{ display: "list-item" }}>
                                  <ListItemsWrapper>
                                    {t("At least one lower case letter")}
                                    {values?.password?.match(
                                      LowerCaseCharacter
                                    ) ? (
                                      <CheckIconWrapper />
                                    ) : (
                                      <ClearIconWrapper />
                                    )}
                                  </ListItemsWrapper>
                                </ListItem>
                                <ListItem sx={{ display: "list-item" }}>
                                  <ListItemsWrapper>
                                    {t("At least one digit")}
                                    {values?.password?.match(
                                      AtLeastOneDigit
                                    ) ? (
                                      <CheckIconWrapper />
                                    ) : (
                                      <ClearIconWrapper />
                                    )}
                                  </ListItemsWrapper>
                                </ListItem>
                                <ListItem sx={{ display: "list-item" }}>
                                  <ListItemsWrapper>
                                    {t(
                                      "At least one special character (Ex. !@#$%)"
                                    )}
                                    {values?.password?.match(
                                      SpecialCharacter
                                    ) ? (
                                      <CheckIconWrapper />
                                    ) : (
                                      <ClearIconWrapper />
                                    )}
                                  </ListItemsWrapper>
                                </ListItem>
                              </ListWrapper>
                            </>
                          }
                        >
                          <InfoIcon sx={{ ml: 2, fontSize: "1.3rem" }} />
                        </HtmlTooltip>
                      )}
                    </LabelWrapper>
                  }
                  placeholder={"Type the password"}
                  fullWidth
                  margin="normal"
                  name="password"
                  type="password"
                  disabled={edit}
                />

                {!edit && (
                  <TextField
                    placeholder={"Retype the password"}
                    label={"Retype password"}
                    name="password2"
                    type="password"
                    disabled={edit}
                  />
                )}

                {/* <TextField label={"User role"} name="userRole" type="select" /> */}
              </Grid>
              <Grid
                container
                xs={6}
                justifyContent="start"
                flexDirection="row"
                sx={{ flexDirection: ["column", "column", "column", "row"] }}
              >
                <Grid item xs={2} />
                <Grid item xs={1} />
                <Grid item xs={2} />
              </Grid>
            </Grid>
            <Grid container justifyContent="left">
              <PrimaryButton
                disabled={!dirty}
                onClick={submitForm}
                sx={{ my: { xs: 5, md: 10 }, mr: { xs: 1, md: 8 } }}
              >
                Save
              </PrimaryButton>
              <PrimaryButton
                onClick={() => navigate("/users")}
                sx={{ my: { xs: 5, md: 10 } }}
              >
                Cancel
              </PrimaryButton>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default UserForm;
